/**
 * readerPwaInstall.js — Reader PWA install routing utility
 *
 * The Indie Reader PWA lives at scope /reader/ on crownrose.com.
 * Its beforeinstallprompt event therefore only fires when the browser's
 * active page is within that scope.  This module handles the three
 * distinct situations a user can be in when they click "Install Reader App":
 *
 *   1. Already on a /reader/* page + native prompt captured
 *      → call prompt() directly                       (native, instant)
 *
 *   2. On any main-site page with a Chrome-family browser
 *      → relay: navigate to /reader/?pwa_install=1&return=<url>
 *        reader/index.html captures the prompt there and auto-triggers it,
 *        then redirects the user back to the originating page.
 *
 *   3. iOS Safari, Firefox, macOS Safari, or any browser without
 *        beforeinstallprompt support
 *      → call onShowGuide() so the caller shows InstallGuideSheet  (manual)
 *
 * The global window.__reader_pwa_prompt is populated by an inline script
 * in reader/index.html so it is available before the Expo bundle loads.
 */

/**
 * Returns true if the current page was opened by a main-site install relay
 * (i.e. URL contains ?pwa_install=1).
 */
export function isInstallRelay() {
  try {
    return new URLSearchParams(window.location.search).get('pwa_install') === '1';
  } catch {
    return false;
  }
}

/**
 * Returns true if the Reader PWA is already running in standalone/installed mode.
 */
export function isReaderInstalled() {
  try {
    return (
      window.matchMedia('(display-mode: standalone)').matches ||
      navigator.standalone === true
    );
  } catch {
    return false;
  }
}

/**
 * Decide the install strategy and execute it.
 *
 * @param {object}   platform     - result of detectPlatform() from PwaInstallCard
 * @param {Function} onShowGuide  - called when the manual InstallGuideSheet should open
 */
export function triggerReaderInstall(platform, onShowGuide) {
  const onReaderPage = window.location.pathname.startsWith('/reader');

  // ── 1. Native prompt available (user is already on a /reader/* page) ──────
  //    window.__reader_pwa_prompt is set by reader/index.html's early-capture script.
  if (onReaderPage && window.__reader_pwa_prompt) {
    const prompt = window.__reader_pwa_prompt;
    window.__reader_pwa_prompt = null;
    prompt.prompt();
    prompt.userChoice.catch(() => {});
    return;
  }

  // ── 2. Chrome-family browser on a main-site page ──────────────────────────
  //    Navigate to the reader page so the correct PWA scope is active, and pass
  //    the originating URL so the user is sent back after install/dismiss.
  const isChromeLike =
    platform.androidChrome  ||
    platform.androidSamsung ||
    platform.desktopChrome  ||
    platform.desktopEdge    ||
    platform.desktopBrave;

  if (!onReaderPage && isChromeLike) {
    const returnUrl = encodeURIComponent(window.location.href);
    window.location.href = `/reader/?pwa_install=1&return=${returnUrl}`;
    return;
  }

  // ── 3. iOS / Firefox / macOS Safari / other ───────────────────────────────
  //    No beforeinstallprompt support — show the step-by-step guide.
  if (typeof onShowGuide === 'function') onShowGuide();
}
